import { quarterBoundsInTz } from '../timezone';
export { qiInstrumentScoreBands } from './instruments';
export * from './workforce-turnover';

/**
 * The calculation version is persisted with every server-side derivation.  It
 * deliberately changes only when a reviewed counting rule changes, so a past
 * lodged period can always be explained using the rule that produced it.
 */
export const QI_DERIVATION_VERSION = 'QI_DERIVATION_V2';

/**
 * Every calculation version a stored or exported result may carry. V1 = falls-with-major-injury
 * judged per incident against the primary resident (any affected person's YES counted that
 * resident; a failed outcome read silently fell back to severity). V2 (care incidents R1 Codex
 * F04/F10) = the distinct set of residents whose own recorded outcome is YES, staff/external/
 * unknown persons excluded, and unavailable outcome evidence disclosed instead of replaced by
 * severity. Old V1 records stay readable: never narrow this union.
 */
export const QI_DERIVATION_VERSIONS = ['QI_DERIVATION_V1', 'QI_DERIVATION_V2'] as const;
export type QiDerivationVersion = (typeof QI_DERIVATION_VERSIONS)[number];

export function isQiDerivationVersion(value: unknown): value is QiDerivationVersion {
  return typeof value === 'string' && (QI_DERIVATION_VERSIONS as readonly string[]).includes(value);
}

export type QiDerivedIndicator =
  | 'falls'
  | 'falls_major_injury'
  | 'pressure_injuries_stage_1'
  | 'pressure_injuries_stage_2'
  | 'pressure_injuries_stage_3'
  | 'pressure_injuries_stage_4'
  | 'pressure_injuries_unstageable'
  | 'pressure_injuries_deep_tissue'
  | 'pressure_injuries_mucosal';

export type QiManualIndicator =
  | 'adl_decline'
  | 'incontinence'
  | 'incontinence_care_plan'
  | 'hospitalisation'
  | 'consumer_experience_positive'
  | 'quality_of_life_positive'
  | 'restrictive_practices'
  | 'unplanned_weight_loss'
  | 'medication_polypharmacy'
  | 'antipsychotic_use'
  | 'workforce';

export type QiIndicator = QiDerivedIndicator | QiManualIndicator;

export type QiPeriod = {
  label: string;
  startUtc: string;
  endUtcExclusive: string;
  /** Optional census instant for prevalence indicators; defaults to period end. */
  censusUtc?: string;
  timezone: string;
};

export type QiIncidentInput = {
  id: string;
  facilityId: string | null;
  clientId: string | null;
  /** NULL = type not yet known (care incidents R1 D-1). */
  incidentType: string | null;
  /** NULL = severity not yet assessed (care incidents R1 D-1). */
  severity: string | null;
  occurredAt: string;
  /**
   * Care incidents R1 S4 (D-8) / Codex F04: one entry per current AFFECTED person on a FALL
   * incident, with that person's identity (`personKind` CLIENT / STAFF / EXTERNAL / UNKNOWN and
   * `clientId` for a resident) and outcome (`incident_involved_persons.fall_major_injury`: YES /
   * NO / UNKNOWN, or null when not recorded). Only a resident's own outcome can count that
   * resident. Omitted or empty = no outcome recorded, and the legacy severity fallback applies.
   */
  fallPersonOutcomes?: ReadonlyArray<QiFallPersonOutcome> | null;
  /**
   * Codex F10: true when the outcome read for this fall FAILED. The fall is then neither judged
   * on severity nor counted; it is disclosed as "outcome evidence unavailable".
   */
  fallOutcomeEvidenceUnavailable?: boolean;
  /**
   * QI Program Manual Part A (September 2026) s 12.2: YES = the fall happened while the person was
   * away from the aged care home and not under direct supervision of staff, so its major injury is
   * not included. NO / UNKNOWN / null are included; UNKNOWN and null are disclosed as not recorded.
   */
  fallAwayUnsupervised?: string | null;
};

/** One affected person's fall outcome, with the identity that decides who it can count. */
export type QiFallPersonOutcome = {
  /** CLIENT (a resident), STAFF, EXTERNAL or UNKNOWN. Anything but CLIENT never counts. */
  personKind: string | null;
  /** The resident (clients.id) for a CLIENT person; null otherwise. */
  clientId: string | null;
  /** YES / NO / UNKNOWN, or null when not recorded. */
  outcome: string | null;
};

/** How a fall was judged for the falls-with-major-injury QI (care incidents R1 S4, D-8). */
export type FallMajorInjuryAssessment = {
  /** At least one resident qualifies (`qualifyingClientIds` is not empty). */
  major: boolean;
  /**
   * PERSON_OUTCOME: at least one affected person has an outcome recorded.
   * SEVERITY_FALLBACK: no affected person has an outcome recorded (legacy rule).
   * EVIDENCE_UNAVAILABLE: the outcome read failed; never judged on severity (Codex F10).
   */
  basis: 'PERSON_OUTCOME' | 'SEVERITY_FALLBACK' | 'EVIDENCE_UNAVAILABLE';
  /** The distinct residents this fall counts for the indicator (Codex F04). */
  qualifyingClientIds: string[];
  /** True when outcomes are recorded but no resident is YES and at least one resident is UNKNOWN. */
  outcomeUnknown: boolean;
  /** Residents on a PERSON_OUTCOME fall whose own outcome is not yet recorded (not counted). */
  residentsOutcomeNotRecorded: number;
  /** True when the fallback ran on a NULL ("not yet assessed") severity. */
  severityUnassessed: boolean;
  /** s 12.2: the fall was away from the home and not under direct staff supervision (excluded). */
  awayUnsupervised: boolean;
  /** s 12.2 attribute not recorded (null) or recorded as UNKNOWN: the fall stays included. */
  awayStatusNotRecorded: boolean;
};

export type QiWoundInput = {
  id: string;
  facilityId: string | null;
  clientId: string | null;
  woundType: string;
  stage: string | null;
  onsetAt: string;
  healedAt: string | null;
  healedDate: string | null;
  status: string;
};

/** A controlled/manual QI ledger row. No free-text clinical narrative crosses this boundary. */
export type QiManualDomainInput = {
  indicator: QiManualIndicator;
  numerator: number;
  denominator: number;
  reason: string | null;
};

/** The denominator is a census snapshot, never an inferred count of source rows. */
export type QiCensusInput = {
  facilityId: string | null;
  residentCount: number | null;
  manualReason: string | null;
  /**
   * QI Program Manual Part A s 12.3.2: individuals absent from accessing funded aged care
   * services throughout the reporting period (for example hospitalised for the whole period).
   * They are excluded from the falls assessment and reported separately. Null = not recorded.
   */
  absentThroughoutCount?: number | null;
};

export type QiSourceCounts = Record<string, number>;

export type QiExclusion = {
  code:
    | 'OUTSIDE_PERIOD'
    | 'OTHER_FACILITY'
    | 'WRONG_INCIDENT_TYPE'
    | 'NOT_MAJOR_INJURY'
    | 'MISSING_CLIENT'
    | 'NOT_PRESSURE_INJURY'
    | 'NOT_ACTIVE_ON_CENSUS'
    | 'UNKNOWN_STAGE'
    | 'MISSING_DENOMINATOR'
    | 'MANUAL_NOT_ENTERED'
    | 'AWAY_UNSUPERVISED'
    | 'OUTCOME_EVIDENCE_UNAVAILABLE';
  count: number;
};

export type QiDerivationResult = {
  indicator: QiIndicator;
  period: QiPeriod;
  facilityId: string | null;
  numerator: number;
  denominator: number;
  method: 'DERIVED' | 'MANUAL';
  sourceCounts: QiSourceCounts;
  exclusions: QiExclusion[];
  /** The rule that produced this result; stored V1 results remain valid input. */
  version: QiDerivationVersion;
};

export type QiDerivationInput = {
  period: QiPeriod;
  facilityId: string | null;
  incidents: readonly QiIncidentInput[];
  wounds: readonly QiWoundInput[];
  manualDomains: readonly QiManualDomainInput[];
  census: readonly QiCensusInput[];
};

const PRESSURE_STAGE_INDICATORS: ReadonlyArray<readonly [string, QiDerivedIndicator]> = [
  ['STAGE_1', 'pressure_injuries_stage_1'],
  ['STAGE_2', 'pressure_injuries_stage_2'],
  ['STAGE_3', 'pressure_injuries_stage_3'],
  ['STAGE_4', 'pressure_injuries_stage_4'],
  ['UNSTAGEABLE', 'pressure_injuries_unstageable'],
  ['DEEP_TISSUE', 'pressure_injuries_deep_tissue'],
  ['MUCOSAL', 'pressure_injuries_mucosal'],
];

const MAJOR_INJURY_SEVERITIES = new Set(['CRITICAL', 'HIGH']);
const RECORDED_FALL_OUTCOMES = new Set(['YES', 'NO', 'UNKNOWN']);

/**
 * The one falls-with-major-injury rule (care incidents R1 S4, D-8; Codex F04/F10). Every QI,
 * benchmark and dashboard consumer calls this, never its own formula.
 *
 * - When any affected person has a recorded outcome, the fall counts exactly the residents
 *   (personKind CLIENT with a client id) whose OWN outcome is YES. A staff, external or unknown
 *   person's YES never counts a resident, and two injured residents count as two. Major injury
 *   (National Aged Care QI Program Manual Part A, September 2026, s 12.2): bone fracture, joint
 *   dislocation, closed head injury with altered consciousness, or subdural haematoma. A fall is
 *   "coming to rest inadvertently on the ground or floor or other lower level", including falls
 *   from a low bed and onto a crash mat.
 * - s 12.2 exclusion: a fall away from the aged care home and not under direct staff supervision
 *   (`fallAwayUnsupervised === 'YES'`) is never a major-injury fall for the indicator. UNKNOWN or
 *   not recorded stays included and is disclosed.
 * - When no outcome is recorded on any affected person (every fall saved before S4, and any fall
 *   nobody has reviewed), the legacy rule applies to the incident's resident: HIGH or CRITICAL
 *   severity is major. Such a fall is reported as "outcome not recorded" for review.
 * - When the outcome read failed (`fallOutcomeEvidenceUnavailable`), the fall is not judged at
 *   all: severity is never substituted for evidence that may say NO. It is disclosed instead.
 */
export function assessFallMajorInjury(incident: {
  severity: string | null | undefined;
  clientId?: string | null;
  fallPersonOutcomes?: ReadonlyArray<QiFallPersonOutcome> | null;
  fallOutcomeEvidenceUnavailable?: boolean;
  fallAwayUnsupervised?: string | null;
}): FallMajorInjuryAssessment {
  const awayUnsupervised = incident.fallAwayUnsupervised === 'YES';
  const awayStatusNotRecorded =
    incident.fallAwayUnsupervised !== 'YES' && incident.fallAwayUnsupervised !== 'NO';
  const base = {
    outcomeUnknown: false,
    residentsOutcomeNotRecorded: 0,
    severityUnassessed: false,
    awayUnsupervised,
    awayStatusNotRecorded,
  };
  if (incident.fallOutcomeEvidenceUnavailable === true) {
    return { ...base, major: false, basis: 'EVIDENCE_UNAVAILABLE', qualifyingClientIds: [] };
  }
  const people = incident.fallPersonOutcomes ?? [];
  const isRecorded = (value: string | null) => value != null && RECORDED_FALL_OUTCOMES.has(value);
  if (people.some((person) => isRecorded(person.outcome))) {
    const residents = people.filter(
      (person): person is QiFallPersonOutcome & { clientId: string } =>
        person.personKind === 'CLIENT' &&
        typeof person.clientId === 'string' &&
        person.clientId !== '',
    );
    const injured = new Set(
      residents.filter((person) => person.outcome === 'YES').map((person) => person.clientId),
    );
    const qualifyingClientIds = awayUnsupervised ? [] : [...injured].sort();
    return {
      ...base,
      major: qualifyingClientIds.length > 0,
      basis: 'PERSON_OUTCOME',
      qualifyingClientIds,
      outcomeUnknown:
        injured.size === 0 && residents.some((person) => person.outcome === 'UNKNOWN'),
      residentsOutcomeNotRecorded: new Set(
        residents
          .filter((person) => !isRecorded(person.outcome))
          .map((person) => person.clientId)
          .filter((clientId) => !injured.has(clientId)),
      ).size,
    };
  }
  const severity = incident.severity ?? null;
  const severityMajor =
    severity != null && MAJOR_INJURY_SEVERITIES.has(severity) && !awayUnsupervised;
  const clientId = incident.clientId ?? null;
  return {
    ...base,
    // The legacy rule judged the incident; it can only attribute the incident's own resident.
    major: severityMajor,
    basis: 'SEVERITY_FALLBACK',
    qualifyingClientIds: severityMajor && clientId ? [clientId] : [],
    severityUnassessed: severity == null || severity === '',
  };
}

function countExclusions(entries: Iterable<string>): QiExclusion[] {
  const counts = new Map<string, number>();
  for (const entry of entries) {
    counts.set(entry, (counts.get(entry) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([code, count]) => ({ code: code as QiExclusion['code'], count }));
}

function inFacility(facilityId: string | null, rowFacilityId: string | null): boolean {
  return facilityId === null || facilityId === rowFacilityId;
}

function isWithinPeriod(timestamp: string, period: QiPeriod): boolean {
  const value = Date.parse(timestamp);
  return (
    Number.isFinite(value) &&
    value >= Date.parse(period.startUtc) &&
    value < Date.parse(period.endUtcExclusive)
  );
}

function isActiveOnCensus(wound: QiWoundInput, censusAt: string): boolean {
  const onset = Date.parse(wound.onsetAt);
  const census = Date.parse(censusAt);
  const healed = wound.healedAt ?? wound.healedDate;
  const healedTime = healed ? Date.parse(healed) : Number.NaN;
  return (
    Number.isFinite(onset) &&
    Number.isFinite(census) &&
    onset <= census &&
    (Number.isFinite(healedTime) ? healedTime > census : wound.status !== 'HEALED')
  );
}

function denominatorFor(input: QiDerivationInput): {
  denominator: number;
  exclusions: QiExclusion[];
} {
  const relevant = input.census.filter((row) => inFacility(input.facilityId, row.facilityId));
  const count = relevant.reduce((total, row) => total + (row.residentCount ?? 0), 0);
  return {
    denominator: count,
    exclusions: count > 0 ? [] : [{ code: 'MISSING_DENOMINATOR', count: 1 }],
  };
}

/**
 * QI Program Manual Part A s 12.3.1–12.3.2: the falls assessment covers every individual except
 * those absent from funded care throughout the period, who are reported separately (Table 12
 * row 2). When the census carries no absence count the denominator is unchanged and the result
 * says so (`absence_recorded: 0`).
 */
function fallsAssessedDenominator(input: QiDerivationInput): {
  denominator: number;
  exclusions: QiExclusion[];
  absent: number;
  absenceRecorded: boolean;
} {
  const base = denominatorFor(input);
  const relevant = input.census.filter((row) => inFacility(input.facilityId, row.facilityId));
  const recordedRows = relevant.filter(
    (row) => typeof row.absentThroughoutCount === 'number' && row.absentThroughoutCount >= 0,
  );
  const absent = recordedRows.reduce((total, row) => total + (row.absentThroughoutCount ?? 0), 0);
  return {
    denominator: Math.max(0, base.denominator - absent),
    exclusions: base.exclusions,
    absent,
    absenceRecorded: relevant.length > 0 && recordedRows.length === relevant.length,
  };
}

function result(
  input: QiDerivationInput,
  indicator: QiIndicator,
  numerator: number,
  denominator: number,
  method: 'DERIVED' | 'MANUAL',
  sourceCounts: QiSourceCounts,
  exclusions: QiExclusion[],
): QiDerivationResult {
  return {
    indicator,
    period: input.period,
    facilityId: input.facilityId,
    numerator,
    denominator,
    method,
    sourceCounts,
    exclusions,
    version: QI_DERIVATION_VERSION,
  };
}

/**
 * Produces local-quarter UTC bounds without consulting a clock. Callers supply
 * the year, quarter and configured tenant timezone, which makes DST edges a
 * deterministic input instead of a machine-timezone accident.
 */
export function qiQuarterPeriod(year: number, quarter: 1 | 2 | 3 | 4, timezone: string): QiPeriod {
  const bounds = quarterBoundsInTz(year, quarter, timezone);
  return {
    label: `${year}-Q${quarter}`,
    startUtc: bounds.startUtc,
    endUtcExclusive: bounds.endUtcExclusive,
    timezone,
  };
}

/**
 * Falls retain the dashboard's existing event rule: every FALL incident in the
 * tenant-local reporting period is counted. This is not represented as a QI
 * Program clinical definition; the rule remains DEFINITION_UNVERIFIED until a
 * reviewed authority replaces the current product behaviour.
 */
export function deriveFalls(input: QiDerivationInput): QiDerivationResult {
  const excluded: string[] = [];
  let numerator = 0;
  for (const incident of input.incidents) {
    if (!inFacility(input.facilityId, incident.facilityId)) {
      excluded.push('OTHER_FACILITY');
    } else if (incident.incidentType !== 'FALL') {
      excluded.push('WRONG_INCIDENT_TYPE');
    } else if (!isWithinPeriod(incident.occurredAt, input.period)) {
      excluded.push('OUTSIDE_PERIOD');
    } else {
      numerator += 1;
    }
  }
  const denominator = denominatorFor(input);
  return result(
    input,
    'falls',
    numerator,
    denominator.denominator,
    'DERIVED',
    { incidents: input.incidents.length, qualifying_incidents: numerator },
    [...countExclusions(excluded), ...denominator.exclusions],
  );
}

/**
 * Falls with major injury (Codex F04): the numerator is the DISTINCT set of residents with at
 * least one in-period qualifying FALL under `assessFallMajorInjury`. A resident counts once
 * however many qualifying falls they had; two injured residents on one fall count twice; a
 * staff, external or unknown person never counts.
 *
 * Source counts make the review visible before lodgement (D-8):
 * - `outcome_not_recorded_falls`: the severity fallback was used;
 * - `severity_fallback_major_falls`: falls counted as major on severity alone;
 * - `outcome_unknown_falls`: outcomes are recorded as unknown, so the fall is not counted;
 * - `resident_outcome_not_recorded`: residents on reviewed falls whose own outcome is still blank;
 * - `outcome_evidence_unavailable_falls`: the outcome read failed, so the fall was not judged
 *   (Codex F10; never replaced by severity);
 * - `unassessed_falls`: no outcome and no severity, so the fall is not major (S3);
 * - `away_unsupervised_excluded_falls`: s 12.2 exclusion applied (away and unsupervised);
 * - `away_status_not_recorded_major_falls`: counted major falls whose s 12.2 status is UNKNOWN or
 *   not recorded (included, disclosed for review);
 * - `absent_throughout_period` / `absence_recorded`: s 12.3.2 exclusion from the denominator.
 */
export function deriveFallsWithMajorInjury(input: QiDerivationInput): QiDerivationResult {
  const excluded: string[] = [];
  const clients = new Set<string>();
  let unassessedFalls = 0;
  let outcomeNotRecorded = 0;
  let severityFallbackMajor = 0;
  let outcomeUnknown = 0;
  let residentOutcomeNotRecorded = 0;
  let evidenceUnavailable = 0;
  let awayUnsupervisedExcluded = 0;
  let awayStatusNotRecordedMajor = 0;
  for (const incident of input.incidents) {
    if (!inFacility(input.facilityId, incident.facilityId)) {
      excluded.push('OTHER_FACILITY');
    } else if (incident.incidentType !== 'FALL') {
      excluded.push('WRONG_INCIDENT_TYPE');
    } else if (!isWithinPeriod(incident.occurredAt, input.period)) {
      excluded.push('OUTSIDE_PERIOD');
    } else {
      const assessment = assessFallMajorInjury(incident);
      if (assessment.basis === 'EVIDENCE_UNAVAILABLE') {
        evidenceUnavailable += 1;
        excluded.push('OUTCOME_EVIDENCE_UNAVAILABLE');
        continue;
      }
      if (assessment.basis === 'SEVERITY_FALLBACK') outcomeNotRecorded += 1;
      if (assessment.outcomeUnknown) outcomeUnknown += 1;
      residentOutcomeNotRecorded += assessment.residentsOutcomeNotRecorded;
      if (assessment.awayUnsupervised) {
        // s 12.2: away from the home and not under direct staff supervision — not included.
        awayUnsupervisedExcluded += 1;
        excluded.push('AWAY_UNSUPERVISED');
      } else if (assessment.basis === 'SEVERITY_FALLBACK' && assessment.severityUnassessed) {
        unassessedFalls += 1;
        excluded.push('NOT_MAJOR_INJURY');
      } else if (assessment.basis === 'SEVERITY_FALLBACK' && !assessment.major) {
        excluded.push('NOT_MAJOR_INJURY');
      } else if (assessment.basis === 'SEVERITY_FALLBACK' && !incident.clientId) {
        severityFallbackMajor += 1;
        if (assessment.awayStatusNotRecorded) awayStatusNotRecordedMajor += 1;
        excluded.push('MISSING_CLIENT');
      } else if (!assessment.major) {
        excluded.push('NOT_MAJOR_INJURY');
      } else {
        if (assessment.basis === 'SEVERITY_FALLBACK') severityFallbackMajor += 1;
        if (assessment.awayStatusNotRecorded) awayStatusNotRecordedMajor += 1;
        for (const clientId of assessment.qualifyingClientIds) clients.add(clientId);
      }
    }
  }
  const denominator = fallsAssessedDenominator(input);
  return result(
    input,
    'falls_major_injury',
    clients.size,
    denominator.denominator,
    'DERIVED',
    {
      incidents: input.incidents.length,
      qualifying_residents: clients.size,
      unassessed_falls: unassessedFalls,
      outcome_not_recorded_falls: outcomeNotRecorded,
      severity_fallback_major_falls: severityFallbackMajor,
      outcome_unknown_falls: outcomeUnknown,
      resident_outcome_not_recorded: residentOutcomeNotRecorded,
      outcome_evidence_unavailable_falls: evidenceUnavailable,
      away_unsupervised_excluded_falls: awayUnsupervisedExcluded,
      away_status_not_recorded_major_falls: awayStatusNotRecordedMajor,
      absent_throughout_period: denominator.absent,
      absence_recorded: denominator.absenceRecorded ? 1 : 0,
    },
    [...countExclusions(excluded), ...denominator.exclusions],
  );
}

/**
 * Returns one prevalence result per stored wound stage. The controlled wounds
 * store wins over incident/free-text records. A wound remains included when it
 * is active at the supplied census instant; it is not a count of new wounds.
 */
export function derivePressureInjuriesByStage(input: QiDerivationInput): QiDerivationResult[] {
  const censusAt = input.period.censusUtc ?? input.period.endUtcExclusive;
  const denominator = denominatorFor(input);
  const stageRows = new Map<string, QiWoundInput[]>();
  const excluded: string[] = [];
  for (const wound of input.wounds) {
    if (!inFacility(input.facilityId, wound.facilityId)) {
      excluded.push('OTHER_FACILITY');
    } else if (wound.woundType !== 'PRESSURE_INJURY') {
      excluded.push('NOT_PRESSURE_INJURY');
    } else if (!isActiveOnCensus(wound, censusAt)) {
      excluded.push('NOT_ACTIVE_ON_CENSUS');
    } else if (
      !wound.stage ||
      !PRESSURE_STAGE_INDICATORS.some(([stage]) => stage === wound.stage)
    ) {
      excluded.push('UNKNOWN_STAGE');
    } else {
      const rows = stageRows.get(wound.stage) ?? [];
      rows.push(wound);
      stageRows.set(wound.stage, rows);
    }
  }
  const commonExclusions = [...countExclusions(excluded), ...denominator.exclusions];
  return PRESSURE_STAGE_INDICATORS.map(([stage, indicator]) => {
    const rows = stageRows.get(stage) ?? [];
    return result(
      input,
      indicator,
      rows.length,
      denominator.denominator,
      'DERIVED',
      { wounds: input.wounds.length, qualifying_wounds: rows.length },
      commonExclusions,
    );
  });
}

/** Manual/specialised ledgers stay pass-through; this function never fabricates zero. */
export function deriveManualIndicator(
  input: QiDerivationInput,
  indicator: QiManualIndicator,
): QiDerivationResult {
  const row = input.manualDomains.find((candidate) => candidate.indicator === indicator);
  if (!row) {
    return result(input, indicator, 0, 0, 'MANUAL', { qi_domain_data: 0 }, [
      { code: 'MANUAL_NOT_ENTERED', count: 1 },
    ]);
  }
  return result(
    input,
    indicator,
    row.numerator,
    row.denominator,
    'MANUAL',
    { qi_domain_data: 1 },
    [],
  );
}

/** Calculates every indicator introduced by this slice in a stable display/persistence order. */
export function deriveQiIndicators(input: QiDerivationInput): QiDerivationResult[] {
  return [
    deriveFalls(input),
    deriveFallsWithMajorInjury(input),
    ...derivePressureInjuriesByStage(input),
    ...(
      [
        'adl_decline',
        'incontinence',
        'incontinence_care_plan',
        'hospitalisation',
        'consumer_experience_positive',
        'quality_of_life_positive',
        'restrictive_practices',
        'unplanned_weight_loss',
        'medication_polypharmacy',
        'antipsychotic_use',
        'workforce',
      ] as const
    ).map((indicator) => deriveManualIndicator(input, indicator)),
  ];
}
